<script setup>
/**
 * What Claude proposed that is not a change set (services spec §8 P-8.4): a whole Personality, an
 * approved message, test numbers, or answers. Nothing here runs Waslo AI except the Keep inside
 * `ClaudeAnswerDrafts`, which is priced and asked first.
 *
 *   prompt             "Use this Personality" → apply `{ use: true }` (refused when it changed since)
 *   approved_message   "Open in the editor" → the editor, filled from Claude's draft; Send stays hers
 *   outbound_settings  "Use these numbers" → the outbound settings' own save, then apply `{ use: true }`
 *   answers            Keep, Keep all, Remove
 */
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';

import ClaudeAnswerDrafts from './ClaudeAnswerDrafts.vue';
import ClaudeChip from './ClaudeChip.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';
import { templatesApi } from '../api/templates.js';
import { messageEditorQuery } from '../helpers/claude.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  suggestion: { type: Object, required: true },
});

const emit = defineEmits(['changed']);

const { t } = useWasloI18n();
const router = useRouter();

const claude = computed(() => props.suggestion.claude);
const busy = ref(false);
const failure = ref('');
const done = ref(false);
const senders = ref(null);
const senderId = ref('');

const loadSenders = async () => {
  if (claude.value?.kind !== 'approved_message' || props.accountId === null)
    return;
  try {
    senders.value = await templatesApi.senders(props.accountId);
    senderId.value = senders.value.length ? String(senders.value[0].id) : '';
  } catch {
    senders.value = [];
  }
};
watch(() => props.suggestion.id, loadSenders, { immediate: true });

const senderOptions = computed(() =>
  (senders.value ?? []).map(s => ({
    value: String(s.id),
    label: s.name || s.phoneNumber,
  }))
);

const run = async work => {
  if (busy.value || props.accountId === null) return;
  busy.value = true;
  failure.value = '';
  try {
    await work();
    done.value = true;
    emit('changed');
  } catch (error) {
    failure.value = error?.message || t('WASLO.CLAUDE.REVIEW.FAILED');
  } finally {
    busy.value = false;
  }
};

const use = () =>
  run(async () => {
    if (claude.value.kind === 'outbound_settings') {
      await wasloApi.updateOutboundSettings(props.accountId, {
        testNumbers: claude.value.testNumbers,
      });
    }
    await wasloApi.applySetupSuggestion(props.accountId, props.suggestion.id, {
      use: true,
    });
  });

const notNow = () =>
  run(() =>
    wasloApi.dismissSetupSuggestion(props.accountId, props.suggestion.id)
  );

const openEditor = () => {
  if (props.accountId === null || senderId.value === '') return;
  router.push({
    name: 'waslo_template_new',
    params: { accountId: String(props.accountId) },
    query: messageEditorQuery(props.suggestion.id, senderId.value),
  });
};
</script>

<template>
  <section
    class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-weak"
    data-waslo-claude-review
    :data-suggestion="suggestion.id"
  >
    <ClaudeChip />

    <!-- Not re-read on a Keep: the drafts follow the narrowing themselves (`ClaudeAnswerDrafts`). -->
    <ClaudeAnswerDrafts
      v-if="claude.kind === 'answers'"
      :account-id="accountId"
      :suggestion="suggestion"
    />

    <template v-else-if="!done">
      <template v-if="claude.kind === 'prompt'">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.CLAUDE.REVIEW.PROMPT') }}
        </p>
        <p
          class="mb-0 text-sm whitespace-pre-wrap text-n-slate-12"
          dir="auto"
          data-waslo-claude-review-prompt
        >
          {{ claude.text }}
        </p>
      </template>

      <template v-else-if="claude.kind === 'approved_message'">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.CLAUDE.REVIEW.MESSAGE') }}
        </p>
        <p class="mb-0 text-sm text-n-slate-12" dir="ltr">
          {{ claude.message.en.body }}
        </p>
        <p class="mb-0 text-sm text-n-slate-12" dir="rtl">
          {{ claude.message.ar.body }}
        </p>
        <WasloSelect
          v-if="senderOptions.length > 1"
          v-model="senderId"
          :label="t('WASLO.CLAUDE.REVIEW.MESSAGE_SENDER')"
          :options="senderOptions"
        />
        <p
          v-else-if="senders && senders.length === 0"
          class="mb-0 text-sm text-n-slate-11"
        >
          {{ t('WASLO.CLAUDE.REVIEW.MESSAGE_NO_NUMBER') }}
        </p>
      </template>

      <template v-else-if="claude.kind === 'outbound_settings'">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.CLAUDE.REVIEW.NUMBERS') }}
        </p>
        <ul
          class="flex flex-col gap-1 mb-0 text-sm ps-5 text-n-slate-12"
          dir="ltr"
        >
          <li v-for="n in claude.testNumbers" :key="n">{{ n }}</li>
        </ul>
      </template>

      <p v-if="claude.why" class="mb-0 text-xs text-n-slate-11">
        <bdi>{{ t('WASLO.CLAUDE.REVIEW.WHY', { why: claude.why }) }}</bdi>
      </p>

      <div class="flex flex-wrap gap-2">
        <Button
          v-if="claude.kind === 'approved_message'"
          :label="t('WASLO.CLAUDE.REVIEW.MESSAGE_OPEN')"
          :disabled="busy || senderId === ''"
          data-waslo-claude-review-open
          @click="openEditor"
        />
        <Button
          v-else
          :label="
            claude.kind === 'prompt'
              ? t('WASLO.CLAUDE.REVIEW.PROMPT_USE')
              : t('WASLO.CLAUDE.REVIEW.NUMBERS_USE')
          "
          :disabled="busy"
          data-waslo-claude-review-use
          @click="use"
        />
        <Button
          :label="t('WASLO.CLAUDE.REVIEW.NOT_NOW')"
          variant="faded"
          color="slate"
          :disabled="busy"
          data-waslo-claude-review-dismiss
          @click="notNow"
        />
      </div>
    </template>

    <p
      v-else
      class="mb-0 text-sm text-n-slate-12"
      data-waslo-claude-review-done
    >
      {{ t('WASLO.CLAUDE.REVIEW.DONE') }}
    </p>

    <p v-if="failure" class="mb-0 text-sm text-n-ruby-11">{{ failure }}</p>
  </section>
</template>
